import React, { useEffect, useState, useCallback } from 'react';

import { Link } from 'react-router-dom';

import { Divider, Modal } from '@material-ui/core';
import { FiEdit3, FiTrash2 } from 'react-icons/fi';

import api from '../../services/api';

import { useAuth } from '../../hooks/auth';
import { useToast } from '../../hooks/toast';

import Toggle from '../../newComponents/Toggle';
import Button from '../../newComponents/Button';

import * as S from './styles';

export interface TerminalFiscal {
  id: number;
  deviceCode: string;
  serieNfce: number;
  serieContingencia: number;
  proximoNumero: number;
  ambiente: number;
  cnpjEmitente?: string | null;
  ativo: boolean;
  /** Emissão de NFC-e neste terminal (E lógico com a flag da empresa). */
  emissaoHabilitada: boolean;
}

const ambienteLabel = (ambiente: number): string =>
  ambiente === 1 ? 'Produção' : 'Homologação';

/**
 * Número da última NFC-e emitida no terminal. A numeração é controlada pela
 * atac-notas, e `proximoNumero` é o espelho já reconciliado pela listagem
 * (RefreshTerminaisNumeracaoService) — a última emitida é sempre a anterior.
 * Mesma leitura da tela de edição do terminal.
 */
const ultimaNotaLabel = (proximoNumero: number): string =>
  proximoNumero > 1 ? `nº ${proximoNumero - 1}` : 'Nenhuma';

const TerminaisFiscais: React.FC = () => {
  const [terminais, setTerminais] = useState<TerminalFiscal[]>([]);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const { user } = useAuth();
  const { addToast } = useToast();

  const loadTerminais = useCallback(async (): Promise<void> => {
    try {
      const response = await api.get(
        `companies/${user.empresa_id}/terminais-fiscais`,
      );
      setTerminais(response.data);
    } catch (err) {
      addToast({ type: 'error', title: 'Erro ao carregar terminais fiscais' });
    }
  }, [user.empresa_id, addToast]);

  useEffect(() => {
    loadTerminais();
  }, [loadTerminais]);

  const handleChangeAtivo = useCallback(
    async (terminalId: number, ativo: boolean) => {
      try {
        const response = await api.patch(
          `companies/${user.empresa_id}/terminais-fiscais/${terminalId}`,
          { ativo },
        );

        addToast({
          type: 'success',
          title: response.data.ativo
            ? 'Terminal ativado'
            : 'Terminal desativado',
        });

        setTerminais((current) =>
          current.map((terminal) =>
            terminal.id === terminalId
              ? { ...terminal, ativo: response.data.ativo }
              : terminal,
          ),
        );
      } catch (err) {
        addToast({ type: 'error', title: 'Erro ao atualizar status' });
      }
    },
    [user.empresa_id, addToast],
  );

  const handleChangeEmissao = useCallback(
    async (terminalId: number, emissaoHabilitada: boolean) => {
      try {
        const response = await api.patch(
          `companies/${user.empresa_id}/terminais-fiscais/${terminalId}`,
          { emissaoHabilitada },
        );

        addToast({
          type: 'success',
          title: response.data.emissaoHabilitada
            ? 'Emissão de NFC-e habilitada no terminal'
            : 'Emissão de NFC-e desabilitada no terminal',
        });

        setTerminais((current) =>
          current.map((terminal) =>
            terminal.id === terminalId
              ? {
                  ...terminal,
                  emissaoHabilitada: response.data.emissaoHabilitada,
                }
              : terminal,
          ),
        );
      } catch (err) {
        addToast({ type: 'error', title: 'Erro ao atualizar a emissão' });
      }
    },
    [user.empresa_id, addToast],
  );

  const handleDelete = useCallback(async () => {
    if (!deleteId) return;

    try {
      await api.delete(
        `companies/${user.empresa_id}/terminais-fiscais/${deleteId}`,
      );

      addToast({ type: 'success', title: 'Terminal excluído' });
      setTerminais((current) =>
        current.filter((terminal) => terminal.id !== deleteId),
      );
    } catch (err) {
      addToast({ type: 'error', title: 'Erro ao excluir terminal' });
    } finally {
      setDeleteId(null);
    }
  }, [deleteId, user.empresa_id, addToast]);

  return (
    <S.Container>
      <div className="header">
        <h2>Terminais</h2>
        <p>
          Cada terminal representa um PDV físico (caixa) que emite NFC-e, com
          série e numeração próprias.
        </p>
      </div>

      <Divider />

      <div className="create_user">
        <Link to="/terminais-fiscais/new">Adicionar Terminal</Link>
      </div>

      <S.ContainerList>
        <S.CustomTable>
          <S.TableHeader>
            <span>Device Code</span>
            <span>Série</span>
            <span>Ambiente</span>
            <span>Próximo Nº</span>
            <span>Última NFC-e</span>
            <span>Editar</span>
            <span>Excluir</span>
            <span>Status</span>
            <span>Emissão NFC-e</span>
          </S.TableHeader>
          <S.TableBody>
            {terminais.map((terminal) => (
              <S.TableRow key={terminal.id}>
                <S.TableCellRow>
                  <p>{terminal.deviceCode}</p>
                </S.TableCellRow>
                <S.TableCellRow>
                  <p>{terminal.serieNfce}</p>
                </S.TableCellRow>
                <S.TableCellRow>
                  <p>{ambienteLabel(terminal.ambiente)}</p>
                </S.TableCellRow>
                <S.TableCellRow>
                  <p>{terminal.proximoNumero}</p>
                </S.TableCellRow>
                <S.TableCellRow>
                  <p>{ultimaNotaLabel(terminal.proximoNumero)}</p>
                </S.TableCellRow>
                <S.TableCellRow>
                  <Link to={`/terminais-fiscais/edit/${terminal.id}`}>
                    <FiEdit3 size={20} />
                  </Link>
                </S.TableCellRow>
                <S.TableCellRow>
                  <button
                    type="button"
                    onClick={() => setDeleteId(terminal.id)}
                  >
                    <FiTrash2 size={20} />
                  </button>
                </S.TableCellRow>
                <S.TableCellRow>
                  <Toggle
                    checked={terminal.ativo}
                    firstText="Inativo"
                    secondText="Ativo"
                    onToggle={(value) => handleChangeAtivo(terminal.id, value)}
                  />
                </S.TableCellRow>
                <S.TableCellRow>
                  <Toggle
                    checked={terminal.emissaoHabilitada !== false}
                    firstText="Não emite"
                    secondText="Emite"
                    onToggle={(value) => handleChangeEmissao(terminal.id, value)}
                  />
                </S.TableCellRow>
              </S.TableRow>
            ))}
          </S.TableBody>
        </S.CustomTable>
      </S.ContainerList>

      <Modal open={!!deleteId} onClose={() => setDeleteId(null)}>
        <S.ModalContent>
          <h1>Excluir terminal fiscal?</h1>
          <p>Esta ação não pode ser desfeita.</p>
          <div className="modal-actions">
            <Button
              variant="outlined"
              full
              type="button"
              onClick={() => setDeleteId(null)}
            >
              Cancelar
            </Button>
            <Button full type="button" onClick={handleDelete}>
              Excluir
            </Button>
          </div>
        </S.ModalContent>
      </Modal>
    </S.Container>
  );
};

export default TerminaisFiscais;
